import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../store/store';
import { runInAction } from 'mobx';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../buttons/IconButton';

/**
 * FiltersChipsList — the canonical "active filters applied" chip strip.
 *
 * Two consumption modes (backward-compatible):
 *
 *   1. **Legacy `listConfig` mode** — KeyContactsTab and any caller passing
 *      `listConfig` get the same behavior as before: chip descriptors are
 *      derived from the field/stateContainer shape and removal mutates the
 *      MobX observable directly via splice.
 *
 *   2. **`chips` mode** — V1 search-results filter rail (TD-2200) and any
 *      future consumer can pass pre-built descriptors directly:
 *      `[{ name, value?, prefix?, source?, icon?, onRemove }]`. When `chips`
 *      is provided, `listConfig` is ignored. Source variants (`'spotlight'`
 *      etc.) add a CSS class hook (`source-spotlight`) for tint variations;
 *      `icon` renders a leading SVG glyph; `prefix` is optional (label-only
 *      chips skip the "prefix: " prefix).
 *
 * Optional `label` and `onClearAll` props let callers customize the chrome.
 */
const FiltersChipsList = observer(
  ({ listConfig = [], chips = null, label = 'Filters applied', onClearAll = null }) => {
    const { utilsStore } = useStore();
    const listRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);

    const items =
      chips ??
      listConfig.reduce((currentItems, currentItem) => {
        const derived = (
          Array.isArray(currentItem.values) && currentItem.values.length
            ? currentItem.values
            : Array.isArray(currentItem.field?.VALUES) && currentItem.field.VALUES.length
              ? currentItem.field.VALUES
              : currentItem.valuesKey || currentItem.field.VALUES_KEY
                ? utilsStore?.options?.[currentItem.valuesKey || currentItem.field.VALUES_KEY] || []
                : []
        )
          .filter(({ value }) =>
            currentItem.stateContainer[currentItem.field.NAME].includes(value)
          )
          .map(({ name, value }) => ({
            name,
            value,
            prefix: currentItem.prefix,
            onRemove: () => {
              runInAction(() => {
                // V2.2 — `.indexOf(value)` is equivalent to the previous
                // `.findIndex((val) => val === value)` and lets Sonar's
                // `prefer-indexOf` rule pass without changing semantics.
                const foundIndex =
                  currentItem.stateContainer[currentItem.field.NAME].indexOf(value);
                if (foundIndex !== -1) {
                  currentItem.stateContainer[currentItem.field.NAME].splice(foundIndex, 1);
                  currentItem.stateContainer[currentItem.field.NAME] =
                    currentItem.stateContainer[currentItem.field.NAME].slice();
                }
              });
            }
          }));

        return [...currentItems, ...derived];
      }, []);

    useEffect(() => {
      const el = listRef.current;
      if (el) {
        const isNowScrollable = el.scrollWidth > el.clientWidth;
        setIsScrollable(isNowScrollable);
      }
    }, [items, utilsStore.windowWidth]);

    if (!items.length) {
      return null;
    }

    return (
      <div className="filters-chips-list-main-wrap">
        <div className="filters-chips-list-text">{label}</div>
        <div
          className="filters-chips-list"
          ref={listRef}
          style={isScrollable ? { paddingBottom: '4px' } : undefined}>
          {items.map(({ name, value, prefix, source, icon, onRemove }, idx) => (
            <div
              className={`filters-chips-item noselect${source ? ' source-' + source : ''}`}
              key={idx + (prefix || '') + (value ?? name)}>
              {icon && (
                <img className="chip-icon" src={icon} alt="" aria-hidden="true" />
              )}
              <div className="display-text">{prefix ? `${prefix}: ${name}` : name}</div>
              <IconButton
                icon={ICON_BUTTON_ICONS.X_LG}
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                onClick={onRemove}
              />
            </div>
          ))}
        </div>
        {onClearAll && (
          <button type="button" className="filters-chips-clear-all" onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>
    );
  }
);

export default FiltersChipsList;
